// src/pages/recommandations/DetailsRecommandation.jsx
import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Spinner, Button, Badge } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import Header from '../../layouts/Header';
import Footer from '../../layouts/Footer';
import { COLORS } from '../../constants/colors';
import toast, { Toaster } from 'react-hot-toast';

// ===== UTILITAIRES =====
const formatDate = (dateString) => {
  if (!dateString) return '-';
  return format(new Date(dateString), 'dd MMMM yyyy', { locale: fr });
};

const formatDateTime = (dateString) => {
  if (!dateString) return '-';
  return format(new Date(dateString), 'dd MMMM yyyy à HH:mm', { locale: fr });
};

const showToast = (message, type = 'success') => {
  const toastOptions = {
    duration: 3000,
    position: 'top-right',
    style: {
      borderRadius: '4px',
      background: '#fff',
      color: '#333',
      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
      padding: '12px 16px',
      fontSize: '0.85rem',
      borderLeft: `4px solid ${type === 'success' ? COLORS.success : type === 'error' ? COLORS.danger : COLORS.warning}`
    },
    iconTheme: {
      primary: type === 'success' ? COLORS.success : type === 'error' ? COLORS.danger : COLORS.warning,
      secondary: '#fff',
    },
  };
  switch(type) {
    case 'success': toast.success(message, toastOptions); break;
    case 'error': toast.error(message, toastOptions); break;
    case 'warning': toast(message, { ...toastOptions, icon: '⚠️' }); break;
    default: toast(message, toastOptions);
  }
};

// ===== STYLES =====
const styles = `
  .dashboard-styled {
    background: #f4f6f9;
    min-height: 100vh;
  }

  /* En-tête moderne */
  .header-modern {
    background: white;
    padding: 1.2rem 1.8rem;
    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
    border-left: 4px solid ${COLORS.secondary};
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
  }

  .header-left {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .header-icon-wrapper {
    width: 48px;
    height: 48px;
    background: ${COLORS.primary};
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    color: white;
    font-size: 1.4rem;
  }

  .gradient-text {
    color: #1e293b;
    font-size: 1.3rem;
    font-weight: 700;
    margin: 0;
  }

  .badge-modern {
    background: #f1f4f9;
    color: #475569;
    padding: 0.3rem 0.8rem;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .badge-modern i {
    color: ${COLORS.secondary};
  }

  .btn-modern-outline {
    border: 1px solid #d1d5db;
    background: white;
    padding: 0.4rem 1.2rem;
    border-radius: 4px;
    font-size: 0.85rem;
    font-weight: 500;
    color: #4b5563;
    transition: all 0.2s;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .btn-modern-outline:hover {
    border-color: ${COLORS.secondary};
    color: ${COLORS.secondary};
    background: #fafafa;
  }

  .btn-modern-primary {
    background: ${COLORS.primary};
    border: none;
    padding: 0.4rem 1.2rem;
    border-radius: 4px;
    font-size: 0.85rem;
    font-weight: 500;
    color: white;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    transition: all 0.2s;
  }
  .btn-modern-primary:hover {
    background: ${COLORS.primaryDark};
    box-shadow: 0 4px 10px rgba(41,90,167,0.3);
  }

  .btn-modern-success {
    background: ${COLORS.success};
    border: none;
    padding: 0.4rem 1.2rem;
    border-radius: 4px;
    font-size: 0.85rem;
    font-weight: 500;
    color: white;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    transition: all 0.2s;
  }
  .btn-modern-success:hover {
    background: #059669;
    box-shadow: 0 4px 10px rgba(16,185,129,0.3);
  }

  .btn-modern-danger {
    background: ${COLORS.danger};
    border: none;
    padding: 0.4rem 1.2rem;
    border-radius: 4px;
    font-size: 0.85rem;
    font-weight: 500;
    color: white;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    transition: all 0.2s;
  }
  .btn-modern-danger:hover {
    background: #dc2626;
    box-shadow: 0 4px 10px rgba(239,68,68,0.3);
  }

  /* Cartes */
  .card-modern {
    border: none;
    border-radius: 4px;
    background: white;
    box-shadow: 0 2px 6px rgba(0,0,0,0.04);
    border-top: 3px solid transparent;
    transition: box-shadow 0.2s;
  }
  .card-modern:hover {
    box-shadow: 0 4px 12px rgba(0,0,0,0.06);
  }
  .card-modern.border-primary {
    border-top-color: ${COLORS.primary};
  }
  .card-modern.border-secondary {
    border-top-color: ${COLORS.secondary};
  }

  .card-header-modern {
    background: transparent;
    border-bottom: 1px solid #e9edf2;
    padding: 0.8rem 1.2rem;
    font-weight: 600;
    color: #1e293b;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  .card-header-modern i {
    color: ${COLORS.secondary};
  }

  .card-body-modern {
    padding: 1.2rem;
  }

  /* Info items */
  .info-item {
    padding: 0.6rem 0.8rem;
    background: #fafbfc;
    border-left: 2px solid ${COLORS.secondary};
    border-radius: 2px;
    margin-bottom: 0.5rem;
  }
  .info-item .info-label {
    font-size: 0.65rem;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    color: #94a3b8;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 0.4rem;
  }
  .info-item .info-label i {
    color: ${COLORS.primary};
  }
  .info-item .info-value {
    font-size: 0.95rem;
    color: #1e293b;
    font-weight: 500;
    padding-left: 1.6rem;
  }
  .info-item-compact {
    padding: 0.4rem 0;
    border-bottom: 1px solid #f1f4f9;
  }
  .info-item-compact:last-child {
    border-bottom: none;
  }

  /* Timeline */
  .timeline {
    position: relative;
    padding-left: 2rem;
  }
  .timeline::before {
    content: '';
    position: absolute;
    left: 7px;
    top: 0;
    bottom: 0;
    width: 2px;
    background: #e2e8f0;
  }
  .timeline-item {
    position: relative;
    margin-bottom: 1.5rem;
  }
  .timeline-item:last-child {
    margin-bottom: 0;
  }
  .timeline-icon {
    position: absolute;
    left: -2rem;
    width: 28px;
    height: 28px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 0.9rem;
    z-index: 2;
  }
  .timeline-content {
    padding-left: 1rem;
  }
  .timeline-date {
    font-size: 0.75rem;
    color: #94a3b8;
    margin-bottom: 0.1rem;
  }
  .timeline-title {
    font-weight: 600;
    color: #1e293b;
    font-size: 0.9rem;
  }
  .timeline-description {
    font-size: 0.8rem;
    color: #64748b;
  }

  /* Statut circle */
  .statut-circle {
    width: 90px;
    height: 90px;
    border-radius: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto;
  }

  /* Loading */
  .loading-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 60vh;
  }
  .loading-ring-wrapper {
    position: relative;
    width: 70px;
    height: 70px;
  }
  .loading-ring {
    position: absolute;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    border: 3px solid #e9edf2;
    border-top-color: ${COLORS.primary};
    animation: spin 0.8s linear infinite;
  }
  .loading-ring-inner {
    position: absolute;
    top: 8px;
    left: 8px;
    right: 8px;
    bottom: 8px;
    border-radius: 50%;
    border: 2px solid #e9edf2;
    border-bottom-color: ${COLORS.secondary};
    animation: spin 0.6s linear infinite reverse;
  }
  @keyframes spin { to { transform: rotate(360deg); } }

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(6px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .fade-in {
    animation: fadeIn 0.3s ease-out;
  }

  @media (max-width: 768px) {
    .header-modern {
      flex-direction: column;
      align-items: stretch;
      gap: 0.8rem;
      padding: 1rem;
    }
    .header-left {
      flex-wrap: wrap;
    }
  }
`;

export default function DetailsRecommandation() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [recommandation, setRecommandation] = useState(null);
  const [eglises, setEglises] = useState([]);
  const [membres, setMembres] = useState([]);

  // ===== FETCH =====
  useEffect(() => {
    fetchEglises();
    fetchMembres();
    fetchRecommandation();
  }, [id]);

  const fetchEglises = async () => {
    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) return;
      const user = JSON.parse(userStr);
      const userId = user.id || user.id_utilisateur;
      const resp = await fetch(`https://josephsbusiness.com/api/eglises.php?user_id=${userId}&type=siege&limit=1000`, {
        headers: { 'Accept': 'application/json' }
      });
      const result = await resp.json();
      if (result.success) setEglises(result.eglises || []);
    } catch (err) { console.error(err); }
  };

  const fetchMembres = async () => {
    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) return;
      const user = JSON.parse(userStr);
      const userId = user.id || user.id_utilisateur;
      const resp = await fetch(`https://josephsbusiness.com/api/membres.php?user_id=${userId}&limit=1000`, {
        headers: { 'Accept': 'application/json' }
      });
      const result = await resp.json();
      if (result.success) setMembres(result.membres || []);
    } catch (err) { console.error(err); }
  };

  const fetchRecommandation = async () => {
    setLoading(true);
    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) { navigate('/pages/signin2'); return; }
      const user = JSON.parse(userStr);
      const userId = user.id || user.id_utilisateur;
      const resp = await fetch(`https://josephsbusiness.com/api/recommandations.php?user_id=${userId}&id=${id}`, {
        headers: { 'Accept': 'application/json' }
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const result = await resp.json();
      if (result.success && result.recommandation) {
        setRecommandation(result.recommandation);
      } else {
        setError(result.message || 'Recommandation non trouvée');
        showToast(result.message || 'Recommandation non trouvée', 'error');
      }
    } catch (err) {
      console.error(err);
      setError(err.message);
      showToast(err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  // ===== HANDLERS =====
  const handleAccepter = async () => {
    try {
      const userStr = localStorage.getItem('user');
      const user = JSON.parse(userStr);
      const userId = user.id || user.id_utilisateur;
      const resp = await fetch(`https://josephsbusiness.com/api/recommandations.php?user_id=${userId}&id=${id}&action=accepter`, {
        method: 'PUT',
        headers: { 'Accept': 'application/json' }
      });
      const result = await resp.json();
      if (result.success) {
        showToast('Recommandation acceptée', 'success');
        fetchRecommandation();
      } else {
        showToast(result.message || 'Erreur', 'error');
      }
    } catch (err) {
      showToast('Erreur: ' + err.message, 'error');
    }
  };

  const handleRefuser = async () => {
    try {
      const userStr = localStorage.getItem('user');
      const user = JSON.parse(userStr);
      const userId = user.id || user.id_utilisateur;
      const resp = await fetch(`https://josephsbusiness.com/api/recommandations.php?user_id=${userId}&id=${id}&action=refuser`, {
        method: 'PUT',
        headers: { 'Accept': 'application/json' }
      });
      const result = await resp.json();
      if (result.success) {
        showToast('Recommandation refusée', 'warning');
        fetchRecommandation();
      } else {
        showToast(result.message || 'Erreur', 'error');
      }
    } catch (err) {
      showToast('Erreur: ' + err.message, 'error');
    }
  };

  const getEgliseName = (matricule) => {
    const eglise = eglises.find(e => e.matricule === matricule);
    return eglise ? eglise.nom : matricule;
  };

  const getEgliseInfo = (matricule) => eglises.find(e => e.matricule === matricule);

  const getMembreInfo = (matricule) => membres.find(m => m.matricule === matricule);

  const getStatutDetails = (statut) => {
    switch(statut) {
      case 'en_attente': return { color: COLORS.warning, icon: 'ri-time-line', label: 'En attente', bg: `${COLORS.warning}15` };
      case 'acceptee': return { color: COLORS.success, icon: 'ri-checkbox-circle-line', label: 'Acceptée', bg: `${COLORS.success}15` };
      case 'refusee': return { color: COLORS.danger, icon: 'ri-close-circle-line', label: 'Refusée', bg: `${COLORS.danger}15` };
      default: return { color: COLORS.textGray, icon: 'ri-question-line', label: statut, bg: `${COLORS.textGray}15` };
    }
  };

  if (loading) {
    return (
      <>
        <Header /><Toaster />
        <div className="main main-app p-3 p-lg-4 dashboard-styled">
          <div className="loading-wrapper">
            <div className="text-center">
              <div className="loading-ring-wrapper mb-3">
                <div className="loading-ring"></div>
                <div className="loading-ring-inner"></div>
              </div>
              <h5 className="fw-bold" style={{ color: COLORS.primaryDark }}>Chargement...</h5>
              <p className="text-muted small">Récupération des détails</p>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (error || !recommandation) {
    return (
      <>
        <Header /><Toaster />
        <div className="main main-app p-3 p-lg-4 dashboard-styled">
          <div className="text-center py-5">
            <i className="ri-error-warning-line" style={{ fontSize: '4rem', color: COLORS.danger }}></i>
            <h4 className="mt-3">Recommandation non trouvée</h4>
            <p className="text-muted">{error || "La recommandation demandée n'existe pas"}</p>
            <Button className="btn-modern-primary" onClick={() => navigate('/recommandations/liste')}>
              <i className="ri-arrow-left-line me-2"></i> Retour à la liste
            </Button>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const statutDetails = getStatutDetails(recommandation.statut);
  const membre = getMembreInfo(recommandation.matri_membre);
  const egliseFrom = getEgliseInfo(recommandation.matri_eglise_from);
  const egliseTo = getEgliseInfo(recommandation.matri_eglise_to);

  return (
    <>
      <style>{styles}</style>
      <Header />
      <Toaster />
      <div className="main main-app p-3 p-lg-4 dashboard-styled">
        <div className="container-fluid px-0">

          {/* En‑tête moderne */}
          <div className="header-modern mb-4 fade-in">
            <div className="header-left">
              <div className="header-icon-wrapper">
                <i className="ri-mail-send-line"></i>
              </div>
              <div>
                <h5 className="gradient-text">Détails de la recommandation</h5>
                <div className="d-flex flex-wrap gap-2">
                  <span className="badge-modern">
                    <i className="ri-barcode-line"></i> {recommandation.matricule}
                  </span>
                  <span className="badge-modern" style={{ background: statutDetails.bg, color: statutDetails.color }}>
                    <i className={statutDetails.icon}></i> {statutDetails.label}
                  </span>
                </div>
              </div>
            </div>
            <div className="d-flex gap-2 flex-wrap">
              {recommandation.statut === 'en_attente' && (
                <>
                  <Button size="sm" className="btn-modern-success" onClick={handleAccepter}>
                    <i className="ri-check-line"></i> Accepter
                  </Button>
                  <Button size="sm" className="btn-modern-danger" onClick={handleRefuser}>
                    <i className="ri-close-line"></i> Refuser
                  </Button>
                </>
              )}
              <Button size="sm" className="btn-modern-primary" onClick={() => navigate(`/recommandations/modifier/${recommandation.id}`)}>
                <i className="ri-edit-line"></i> Modifier
              </Button>
              <Button size="sm" className="btn-modern-outline" onClick={() => navigate('/recommandations/liste')}>
                <i className="ri-arrow-left-line"></i> Retour
              </Button>
            </div>
          </div>

          <Row className="g-4">
            {/* Colonne principale */}
            <Col lg={8}>
              {/* Informations générales */}
              <Card className="card-modern border-primary mb-4">
                <Card.Header className="card-header-modern">
                  <i className="ri-information-line"></i> Informations générales
                </Card.Header>
                <Card.Body className="card-body-modern">
                  <Row className="g-3">
                    <Col md={6}>
                      <div className="info-item">
                        <div className="info-label"><i className="ri-user-line"></i> Membre concerné</div>
                        <div className="info-value">
                          <span className="fw-bold">{membre ? `${membre.nom} ${membre.prenom || ''}` : recommandation.matri_membre}</span>
                          {membre && (
                            <div className="small text-muted mt-1">
                              <i className="ri-mail-line me-1"></i>{membre.email || 'Email non renseigné'}
                              <span className="mx-2">•</span>
                              <i className="ri-phone-line me-1"></i>{membre.contact || 'Tél. non renseigné'}
                            </div>
                          )}
                        </div>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="info-item">
                        <div className="info-label"><i className="ri-calendar-line"></i> Période</div>
                        <div className="info-value">
                          <span className="fw-bold">{recommandation.periode || '-'}</span>
                        </div>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="info-item">
                        <div className="info-label"><i className="ri-building-line"></i> Église d'origine</div>
                        <div className="info-value">
                          <span className="fw-bold">{getEgliseName(recommandation.matri_eglise_from)}</span>
                          {egliseFrom && (
                            <div className="small text-muted">
                              <i className="ri-map-pin-line me-1"></i>
                              {egliseFrom.ville || 'Ville non renseignée'}
                              {egliseFrom.province && `, ${egliseFrom.province}`}
                            </div>
                          )}
                        </div>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="info-item">
                        <div className="info-label"><i className="ri-building-line"></i> Église de destination</div>
                        <div className="info-value">
                          <span className="fw-bold">{getEgliseName(recommandation.matri_eglise_to)}</span>
                          {egliseTo && (
                            <div className="small text-muted">
                              <i className="ri-map-pin-line me-1"></i>
                              {egliseTo.ville || 'Ville non renseignée'}
                              {egliseTo.province && `, ${egliseTo.province}`}
                            </div>
                          )}
                        </div>
                      </div>
                    </Col>
                  </Row>

                  {recommandation.description && (
                    <>
                      <hr className="my-3" style={{ opacity: 0.1 }} />
                      <div className="info-item">
                        <div className="info-label"><i className="ri-file-text-line"></i> Description</div>
                        <div className="info-value">
                          <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>{recommandation.description}</p>
                        </div>
                      </div>
                    </>
                  )}
                </Card.Body>
              </Card>

              {/* Historique */}
              <Card className="card-modern border-secondary">
                <Card.Header className="card-header-modern">
                  <i className="ri-history-line"></i> Historique
                </Card.Header>
                <Card.Body className="card-body-modern">
                  <div className="timeline">
                    <div className="timeline-item">
                      <div className="timeline-icon" style={{ background: COLORS.success }}>
                        <i className="ri-add-line"></i>
                      </div>
                      <div className="timeline-content">
                        <div className="timeline-date">{formatDateTime(recommandation.created_at)}</div>
                        <div className="timeline-title">Création de la recommandation</div>
                        <div className="timeline-description">Recommandation créée avec le statut "En attente"</div>
                      </div>
                    </div>

                    {recommandation.updated_at && recommandation.updated_at !== recommandation.created_at && (
                      <div className="timeline-item">
                        <div className="timeline-icon" style={{ background: COLORS.info }}>
                          <i className="ri-edit-line"></i>
                        </div>
                        <div className="timeline-content">
                          <div className="timeline-date">{formatDateTime(recommandation.updated_at)}</div>
                          <div className="timeline-title">Dernière modification</div>
                        </div>
                      </div>
                    )}

                    {recommandation.statut !== 'en_attente' && (
                      <div className="timeline-item">
                        <div className="timeline-icon" style={{
                          background: recommandation.statut === 'acceptee' ? COLORS.success : COLORS.danger
                        }}>
                          <i className={recommandation.statut === 'acceptee' ? 'ri-check-line' : 'ri-close-line'}></i>
                        </div>
                        <div className="timeline-content">
                          <div className="timeline-date">{formatDateTime(recommandation.updated_at)}</div>
                          <div className="timeline-title">
                            Recommandation {recommandation.statut === 'acceptee' ? 'acceptée' : 'refusée'}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </Card.Body>
              </Card>
            </Col>

            {/* Colonne latérale */}
            <Col lg={4}>
              {/* Statut */}
              <Card className="card-modern border-primary mb-4">
                <Card.Body className="card-body-modern text-center">
                  <div className="statut-circle rounded" style={{ background: statutDetails.bg,borderRadius:'100%' }}>
                    <i className={statutDetails.icon} style={{ fontSize: '42px', color: statutDetails.color }}></i>
                  </div>
                  <h5 className="fw-bold mt-3" style={{ color: statutDetails.color }}>{statutDetails.label}</h5>
                  <p className="text-muted small">Statut actuel</p>

                  {recommandation.statut === 'en_attente' && (
                    <div className="d-flex gap-2 justify-content-center mt-3">
                      <Button size="sm" className="btn-modern-success" onClick={handleAccepter}>
                        <i className="ri-check-line"></i> Accepter
                      </Button>
                      <Button size="sm" className="btn-modern-danger" onClick={handleRefuser}>
                        <i className="ri-close-line"></i> Refuser
                      </Button>
                    </div>
                  )}
                </Card.Body>
              </Card>

              {/* Informations supplémentaires */}
              <Card className="card-modern border-secondary">
                <Card.Header className="card-header-modern">
                  <i className="ri-information-line"></i> Informations supplémentaires
                </Card.Header>
                <Card.Body className="card-body-modern">
                  <div className="info-item-compact">
                    <div className="d-flex justify-content-between">
                      <span className="text-muted small">Matricule</span>
                      <span className="fw-semibold">{recommandation.matricule}</span>
                    </div>
                  </div>
                  <div className="info-item-compact">
                    <div className="d-flex justify-content-between">
                      <span className="text-muted small">Date de création</span>
                      <span className="fw-semibold">{formatDate(recommandation.created_at)}</span>
                    </div>
                  </div>
                  {recommandation.updated_at && (
                    <div className="info-item-compact">
                      <div className="d-flex justify-content-between">
                        <span className="text-muted small">Dernière modification</span>
                        <span className="fw-semibold">{formatDate(recommandation.updated_at)}</span>
                      </div>
                    </div>
                  )}
                  <div className="info-item-compact">
                    <div className="d-flex justify-content-between">
                      <span className="text-muted small">ID</span>
                      <span className="fw-semibold">#{recommandation.id}</span>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Footer />
        </div>
      </div>
    </>
  );
}